// ---- Refference จาก ThreePanelSplitView.tsx
import React, { CSSProperties, useState, useRef, useCallback } from 'react';

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'row',
    height: 'calc(100% - 2.5px)',
    width: '100%',
    overflow: 'hidden',
    backgroundColor: '#f9fafb',
    borderTop: "solid 1px #9ca3af"
  },
  panel: {
    overflow: 'auto',
    position: 'relative',
  },
  leftPanel: {
    backgroundColor: '#f8fafc',
    borderRight: '1px solid #d1d5db'
  },
  rightPanel: {
    backgroundColor: '#ffffff'
  },
  resizer: {
    width: '4px',
    cursor: 'col-resize',
    position: 'relative',
    transition: 'background-color 0.2s',
    userSelect: 'none',
    flexShrink: 0
  },
  resizerDefault: {
    backgroundColor: '#d1d5db'
  },
  resizerHover: {
    backgroundColor: '#60a5fa'
  },
  resizerActive: {
    backgroundColor: '#3b82f6'
  },
  resizerHandle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '12px',
    height: '32px',
    backgroundColor: '#9ca3af',
    borderRadius: '2px',
    opacity: 0,
    transition: 'opacity 0.2s'
  },
  resizerHandleVisible: {
    opacity: 1,
    backgroundColor: '#3b82f6'
  }
};

type TwoPanelSplitViewProps = {
  leftPanel?: React.ReactNode;
  rightPanel?: React.ReactNode;
  defaultLeftWidth?: number; // %
  minPanelWidth?: number; // %
  maxPanelWidth?: number; // %
  onResize?: (leftWidth: number, rightWidth: number) => void;
  className?: string;
};

const TwoPanelSplitView: React.FC<TwoPanelSplitViewProps> = (props) => {
  const [leftWidth, setLeftWidth] = useState(props.defaultLeftWidth || 30);
  const [activeResizer, setActiveResizer] = useState(false);
  const [hovered, setHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setActiveResizer(true);

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - rect.left) / rect.width) * 100;

      const constrained = Math.min(
        Math.max(newLeftWidth, props.minPanelWidth || 15),
        props.maxPanelWidth || 85
      );
      setLeftWidth(constrained);
      if (props.onResize) props.onResize(constrained, 100 - constrained);
    };

    const handleMouseUp = () => {
      setActiveResizer(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [props.minPanelWidth, props.maxPanelWidth, props.onResize]);

  return (
    <div ref={containerRef} style={styles.container} className={props.className || ''}>
      {/* Left Panel */}
      <div
        style={{
          ...styles.panel,
          ...styles.leftPanel,
          width: `${leftWidth}%`
        }}
      >
        {props.leftPanel}
      </div>

      {/* Resizer */}
      <div
        style={{
          ...styles.resizer,
          ...(activeResizer
            ? styles.resizerActive
            : hovered
              ? styles.resizerHover
              : styles.resizerDefault)
        }}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          style={{
            ...styles.resizerHandle,
            ...(hovered ? styles.resizerHandleVisible : {})
          }}
        />
      </div>

      {/* Right Panel */}
      <div
        style={{
          ...styles.panel,
          ...styles.rightPanel,
          width: `${100 - leftWidth}%`
        }}
      >
        {props.rightPanel}
      </div>
    </div>
  );
};

// const globalStyles = document.createElement('style');
// globalStyles.innerHTML = `
//   * {
//     margin: 0;
//     padding: 0;
//     box-sizing: border-box;
//   }

//   body {
//     font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
//   }
// `;
// document.head.appendChild(globalStyles);

export default TwoPanelSplitView;
